import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form, Input, Modal, ModalProps } from 'antd'
import { useEffect } from 'react'

import {
  useStocksServiceCreateProducerEndpoint,
  useStocksServiceGetProducerEndpoint,
  useStocksServiceGetProducerListEndpointKey,
  useStocksServiceUpdateProducerEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { CountrySelect, message, SaveConfirm } from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  id?: string
  onCancel: () => void
} & ModalProps

const getModalText = (hasId: boolean) => {
  return {
    title: `medicine_register.producers.${hasId ? 'edit' : 'add'}_company`,
    button: `common.${hasId ? 'edit' : 'add'}`
  }
}

export const ProducersModal = ({ id, onCancel, ...props }: Props) => {
  const { t } = useLocales()
  const [form] = Form.useForm()

  const queryClient = useQueryClient()

  const { title, button } = getModalText(!!id)

  const { data } = useStocksServiceGetProducerEndpoint(
    { id: String(id) },
    [id],
    { enabled: !!id }
  )

  const { mutate: edit, isPending: isEditing } =
    useStocksServiceUpdateProducerEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => onSuccessMutation()
    })

  const { mutate: create, isPending: isCreating } =
    useStocksServiceCreateProducerEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => onSuccessMutation()
    })

  const onSubmit = async () => {
    const requestBody = await form.validateFields()

    id ? edit({ requestBody, id }) : create({ requestBody })
  }

  const onSuccessMutation = () => {
    onCancel()
    queryClient.invalidateQueries({
      queryKey: [useStocksServiceGetProducerListEndpointKey]
    })
    message.success(t('common.save_success'))
  }

  useEffect(() => {
    if (data && id) {
      form.setFieldsValue(data)
    }
  }, [id, data])

  return (
    <Modal
      centered
      title={t(title)}
      open
      afterClose={form.resetFields}
      footer={null}
      maskClosable={false}
      onCancel={onCancel}
      {...props}
    >
      <Flex vertical gap={24}>
        <Form layout="vertical" requiredMark={false} form={form}>
          <Flex vertical gap={24}>
            <Form.Item
              name="name"
              label={t('medicine_register.producers.name')}
              rules={requiredRules}
            >
              <Input placeholder={t('medicine_register.producers.name')} />
            </Form.Item>

            <Form.Item
              name="country"
              label={t('common.country')}
              rules={requiredRules}
            >
              <CountrySelect placeholder={t('common.country')} />
            </Form.Item>
          </Flex>
        </Form>

        <Flex gap={16} justify="center">
          <Button onClick={onCancel}>{t('common.cancel')}</Button>

          <SaveConfirm
            title={`${t(button)}?`}
            onConfirm={onSubmit}
            okButtonProps={{ loading: isCreating || isEditing }}
          >
            <Button type="primary">{t(button)}</Button>
          </SaveConfirm>
        </Flex>
      </Flex>
    </Modal>
  )
}
